// Site24x7 RUM Configuration and Implementation
// Production-ready version for staging deployment

// Environment-specific RUM app keys
const RUM_APP_KEYS = {
  production: '8e58e9de2e9d896c626ad1a97ccb2c44',
  staging: '47c3f84d11e65539245985949e5880f1',
  development: '99d10bc0dde221d85afa7d24ef813e75',
}

//Site24x7 RUM Configuration
const RUM_CONFIG = {
  // Base URL for Site24x7 RUM script (European region)
  scriptUrl: 'https://static.site24x7rum.eu/beacon/site24x7rum-min.js?appKey=',

  // Data collection endpoint (European region)
  dataEndpoint: 'https://col.site24x7rum.eu',

  // Global RUM function name (as defined by Site24x7)
  functionName: 's247r',

  // Environment-specific options
  options: {
    enableDebugLogs: false,
    captureErrors: true,
    enableInProduction: true,
    enableInStaging: true,
    enableInDevelopment: false,
  },
}

// Global state
let rumInitialized = false

//Get current environment based on hostname
export const isProduction = () => {
  return window.location.hostname === 'admin.entrecloud.se'
}

export const isStaging = () => {
  // Staging hostname patterns
  const stagingPatterns = ['admin.staging.entrecloud.se', 'staging.entrecloud.se', 'staging']

  const hostname = window.location.hostname
  return stagingPatterns.some((pattern) => hostname.includes(pattern) || hostname === pattern)
}

export const isDevelopment = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}

//Get current environment name
export const getCurrentEnvironment = () => {
  if (isProduction()) return 'production'
  if (isStaging()) return 'staging'
  return 'development'
}

export const getRumAppKey = () => {
  const env = getCurrentEnvironment()
  const key = RUM_APP_KEYS[env]

  if (!key || key.includes('YOUR_')) {
    console.warn(`Site24x7 RUM: No valid app key configured for ${env} environment`)
    return null
  }

  return key
}

//Check if RUM should be initialized in current environment
export const shouldEnableRum = () => {
  const env = getCurrentEnvironment()

  // Force enable in development with URL parameter
  if (env === 'development') {
    return window.location.href.includes('enableRum=true') && RUM_CONFIG.options.enableInDevelopment
  }

  // Check environment-specific settings
  switch (env) {
    case 'production':
      return RUM_CONFIG.options.enableInProduction
    case 'staging':
      return RUM_CONFIG.options.enableInStaging
    default:
      return false
  }
}

//Validate RUM configuration
const validateRumConfig = () => {
  const appKey = getRumAppKey()
  if (!appKey) {
    return { valid: false, error: 'No valid RUM app key configured' }
  }

  if (appKey.includes('YOUR_')) {
    return { valid: false, error: 'RUM app key contains placeholder text' }
  }

  return { valid: true, error: null }
}

/**
 * Initialize Site24x7 RUM monitoring
 * @param {string} appKeyOverride - Optional override for the app key
 * @param {Object} options - Optional configuration overrides
 */
export const initSite24x7Rum = (appKeyOverride, options = {}) => {
  //Prevent multiple initialization
  if (rumInitialized) {
    console.warn('Site24x7 RUM: Already initialized')
    return { success: false, error: 'Already initialized' }
  }

  //Check if RUM should be enabled
  if (!shouldEnableRum()) {
    return { success: false, error: 'RUM disabled for current environment' }
  }

  //Validate configuration
  const validation = validateRumConfig()
  if (!validation.valid && !appKeyOverride) {
    console.error('Site24x7 RUM:', validation.error)
    return { success: false, error: validation.error }
  }

  //Get the app key
  const appKey = appKeyOverride || getRumAppKey()
  const config = { ...RUM_CONFIG.options, ...options }

  if (!appKey) {
    console.error('Site24x7 RUM: No app key available')
    return { success: false, error: 'No app key available' }
  }

  try {
    //inject RUM script dynamically
    injectRumScript(appKey, config)
    rumInitialized = true

    return { success: true, error: null }
  } catch (error) {
    console.error('Site24x7 RUM: Failed to initialize:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Inject the Site24x7 RUM script dynamically
 * Based on official Site24x7 snippet with enhancements
 * @param {string} appKey - The RUM application key
 * @param {Object} config - Configuration options
 */
export const injectRumScript = (appKey, config = {}) => {
  // Site24x7 RUM script injection (official snippet logic)
  ;(function (w, d, src, fn, key, scriptElem, prevOnError) {
    // Only initialize if performance API is available and RUM not already loaded
    if (w.performance && w.performance.timing && w.performance.navigation && !w[fn]) {
      // Define the global RUM function (s247r) to queue calls until script loads
      w[fn] =
        w[fn] ||
        function () {
          ;(w[fn].q = w[fn].q || []).push(arguments)
        }

      // Create script element to load the Site24x7 RUM script
      scriptElem = d.createElement('script')
      scriptElem.async = true
      scriptElem.src = src + key // src URL + your unique App Key

      // Add error handling for script loading
      scriptElem.onerror = function () {
        console.error('Site24x7 RUM: Failed to load script from', src + key)
      }

      scriptElem.onload = function () {
        // Enable custom event tracking once RUM script loads
        w[fn]('trackEvents', true)
      }

      d.getElementsByTagName('head')[0].appendChild(scriptElem)

      // Hook into global error handler to capture JS errors (if enabled)
      if (config.captureErrors !== false) {
        prevOnError = w.onerror
        w.onerror = function (msg, url, line, col, err) {
          if (prevOnError) prevOnError(msg, url, line, col, err)
          err = err || new Error(msg)
          w[fn].q = w[fn].q || []
          w[fn].q.push(['captureException', err])
        }
      }
    }
  })(window, document, RUM_CONFIG.scriptUrl, RUM_CONFIG.functionName, appKey)
}

/**
 * Format user information for RUM tracking
 * @param {string|object} userInfo - User ID string or user object with detailed info
 * @returns {string} - Formatted user identifier
 */
export const formatUserIdentifier = (userInfo) => {
  if (typeof userInfo === 'string') {
    // If it's just a string, use it as-is (backward compatibility)
    return userInfo
  }

  if (typeof userInfo === 'object' && userInfo !== null) {
    const { name, email, id, firstName, lastName, userId } = userInfo

    // Build formatted user identifier: "Name (email) - ID: 123"
    let identifier = ''

    // Add name (prioritize full name, then first+last, then just name)
    if (name) {
      identifier += name
    } else if (firstName && lastName) {
      identifier += `${firstName} ${lastName}`
    } else if (firstName) {
      identifier += firstName
    }

    // Add email in parentheses
    if (email) {
      identifier += identifier ? ` (${email})` : email
    }

    // Add ID at the end (keep it short for Site24x7)
    const finalId = id || userId
    if (finalId) {
      identifier += identifier ? ` - ${finalId}` : `ID: ${finalId}`
    }

    // Sanitize and limit length to avoid Site24x7 issues
    let result = identifier || 'Unknown User'

    // Remove problematic characters that might cause 400 errors
    result = result.replace(/[^\w\s@.-]/g, '') // Keep only alphanumeric, spaces, @, ., -

    return result.length > 100 ? result.substring(0, 97) + '...' : result
  }

  return 'Unknown User'
}

/**
 * Set user ID for RUM tracking with enhanced user information
 * @param {string|object} userInfo - User ID string or user object with detailed info
 * @param {string} userInfo.id - User ID
 * @param {string} userInfo.name - Full name
 * @param {string} userInfo.firstName - First name
 * @param {string} userInfo.lastName - Last name
 * @param {string} userInfo.email - Email address
 * @param {string} userInfo.userId - Alternative user ID field
 */
export const setRumUserId = (userInfo) => {
  if (!rumInitialized) {
    console.warn('Site24x7 RUM: Cannot set user ID - RUM not initialized')
    return false
  }

  if (!window[RUM_CONFIG.functionName] || !userInfo) {
    console.warn('Site24x7 RUM: Cannot set user ID - invalid parameters')
    return false
  }

  try {
    // Format user information for better readability
    const formattedUserId = formatUserIdentifier(userInfo)

    // Use Site24x7's official API method
    window[RUM_CONFIG.functionName]('userId', formattedUserId)

    return true
  } catch (error) {
    console.error('Site24x7 RUM: Failed to set user ID:', error)
    return false
  }
}

/**
 * Add custom breadcrumb for debugging
 * @param {string} message - Breadcrumb message
 * @param {string} category - Category (optional)
 */
export const addRumBreadcrumb = (message, category = 'custom') => {
  if (isRumAvailable()) {
    try {
      window[RUM_CONFIG.functionName]('addBreadcrumb', {
        message,
        category,
        timestamp: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Site24x7 RUM: Failed to add breadcrumb:', error)
    }
  }
}

/**
 * Generate readable and meaningful event names
 * @param {string} elementType - Type of element (button, a, input, etc.)
 * @param {string} elementId - Element ID
 * @param {string} elementText - Element text content
 * @param {object} metadata - Additional metadata
 * @returns {string} - Clean, readable event name
 */
const generateReadableEventName = (elementType, elementId, elementText, metadata = {}) => {
  let eventName = 'click_'

  // Add meaningful element type
  if (elementType === 'button') {
    eventName += 'button_'
  } else if (elementType === 'a') {
    eventName += 'link_'
  } else if (elementType === 'input') {
    eventName += 'input_'
  } else if (elementType === 'form') {
    eventName += 'form_'
  } else {
    eventName += 'element_'
  }

  // Add meaningful identifier (prioritize business context)
  if (elementText && elementText.length > 0 && elementText.length < 30) {
    // Use button text if it's short and meaningful
    eventName += elementText.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_')
  } else if (elementId && elementId.length > 0 && elementId.length < 30 && !elementId.match(/^[a-f0-9]{8,}$/)) {
    // Use ID if it's not a random hash and is reasonably short
    eventName += elementId.replace(/[^a-zA-Z0-9]/g, '_')
  } else if (metadata?.routeName) {
    // Use route name for navigation elements
    eventName += metadata.routeName.replace(/[^a-zA-Z0-9]/g, '_')
  } else if (metadata?.formName) {
    // Use form name for form elements
    eventName += metadata.formName.replace(/[^a-zA-Z0-9]/g, '_')
  } else {
    // Fallback to generic but clean name
    eventName += 'unknown'
  }

  // Clean up and limit length
  eventName = eventName.replace(/_+/g, '_').replace(/^_|_$/g, '')
  return eventName.substring(0, 50) // Limit to 50 characters
}

/**
 * Generate readable navigation event names
 * @param {string} route - Route path
 * @param {object} params - Route parameters (currently unused, reserved for future use)
 * @returns {string} - Clean navigation event name
 */
// eslint-disable-next-line no-unused-vars
const generateReadableNavigationName = (route, params = {}) => {
  let eventName = 'page_'

  // Clean up route path
  if (route && route.length > 0) {
    // Remove leading slash and clean the route
    const cleanRoute = route.replace(/^\/+/, '').replace(/\/+$/, '')

    if (cleanRoute.length === 0) {
      eventName += 'home'
    } else {
      // Convert common patterns to readable names
      let routeName = cleanRoute
        .replace(/\//g, '_')
        .replace(/company\/\d+/g, 'company_details')
        .replace(/user\/\d+/g, 'user_details')
        .replace(/\d+/g, 'details')
        .replace(/[^a-zA-Z0-9_]/g, '_')
        .toLowerCase()

      // Add specific business context
      if (routeName.includes('licenses')) {
        eventName += 'licenses_page'
      } else if (routeName.includes('users')) {
        eventName += 'users_page'
      } else if (routeName.includes('company')) {
        eventName += 'company_page'
      } else if (routeName.includes('questionnaires')) {
        eventName += 'questionnaires_page'
      } else if (routeName.includes('filecontainers')) {
        eventName += 'files_page'
      } else if (routeName.includes('properties')) {
        eventName += 'properties_page'
      } else if (routeName.includes('tenantconfig')) {
        eventName += 'settings_page'
      } else {
        eventName += routeName
      }
    }
  } else {
    eventName += 'unknown'
  }

  // Clean up and limit length
  eventName = eventName.replace(/_+/g, '_').replace(/^_|_$/g, '')
  return eventName.substring(0, 50) // Limit to 50 characters
}

/**
 * Track page navigation (Vue Router integration)
 * @param {string} fromRoute - Previous route path
 * @param {string} toRoute - New route path
 * @param {object} routeParams - Route parameters (optional)
 */
export const trackPageNavigation = (fromRoute, toRoute, routeParams = {}) => {
  if (!isRumAvailable()) return

  try {
    // Track page view with Site24x7 (this is a valid built-in API)
    window[RUM_CONFIG.functionName]('trackPageView', {
      page: toRoute,
      previousPage: fromRoute,
      timestamp: new Date().toISOString(),
      ...routeParams,
    })

    // Also track as custom event for better visibility
    const cleanNavigationName = generateReadableNavigationName(toRoute, routeParams)
    window[RUM_CONFIG.functionName]('addEvent', cleanNavigationName)

    // Add breadcrumb for navigation
    addRumBreadcrumb(`Navigation: ${fromRoute} → ${toRoute}`, 'navigation')
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track page navigation:', error)
  }
}

/**
 * Track user clicks and interactions
 * @param {string} elementType - Type of element clicked (button, link, etc.)
 * @param {string} elementId - Element ID or identifier
 * @param {string} elementText - Element text content
 * @param {object} metadata - Additional metadata
 */
export const trackUserClick = (elementType, elementId, elementText, metadata = {}) => {
  if (!isRumAvailable()) return

  try {
    // Create more meaningful and readable event names
    const cleanEventName = generateReadableEventName(elementType, elementId, elementText, metadata)
    window[RUM_CONFIG.functionName]('addEvent', cleanEventName)

    // Add breadcrumb for click
    addRumBreadcrumb(`Click: ${cleanEventName}`, 'interaction')
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track user click:', error)
  }
}

/**
 * Track form interactions
 * @param {string} formId - Form identifier
 * @param {string} action - Form action (submit, validation_error, etc.)
 */
export const trackFormInteraction = (formId, action) => {
  if (!isRumAvailable()) return

  try {
    // Use correct Site24x7 custom events API
    const eventType = `form_${action}_${formId}`.replace(/[^a-zA-Z0-9_]/g, '_')
    window[RUM_CONFIG.functionName]('addEvent', eventType)

    addRumBreadcrumb(`Form: ${formId} - ${action}`, 'form')
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track form interaction:', error)
  }
}

/**
 * Track user session data
 * @param {object} sessionData - Session information
 */
export const trackUserSession = (sessionData = {}) => {
  if (!isRumAvailable()) return

  try {
    const sessionInfo = {
      sessionId: sessionData.sessionId || 'unknown',
      userId: sessionData.userId || 'anonymous',
      userRole: sessionData.userRole || 'guest',
      companyId: sessionData.companyId || null,
      loginTime: sessionData.loginTime || new Date().toISOString(),
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...sessionData,
    }

    // Set user ID in RUM with enhanced user information
    if (sessionInfo.userId && sessionInfo.userId !== 'anonymous') {
      // Create comprehensive user info object for formatting
      const userInfo = {
        id: sessionInfo.userId,
        name: sessionInfo.userName || sessionInfo.fullName,
        firstName: sessionInfo.firstName,
        lastName: sessionInfo.lastName,
        email: sessionInfo.email || sessionInfo.userEmail,
        userId: sessionInfo.userId,
      }

      setRumUserId(userInfo)
    }

    // Track session start event
    window[RUM_CONFIG.functionName]('addEvent', 'session_start')

    // Create formatted user identifier for breadcrumb
    const formattedUserId = formatUserIdentifier(sessionInfo)
    addRumBreadcrumb(`Session: ${formattedUserId} (${sessionInfo.userRole})`, 'session')
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track user session:', error)
  }
}

/**
 * Track page exit/leave events
 * @param {string} exitReason - Reason for leaving (navigation, close, refresh, etc.)
 * @param {object} pageMetrics - Page performance metrics
 */
export const trackPageExit = (exitReason = 'unknown', pageMetrics = {}) => {
  if (!isRumAvailable()) return

  try {
    const performanceData = {
      timeOnPage: Date.now() - (window.pageLoadTime || Date.now()),
      pageUrl: window.location.href,
      exitReason,
      scrollDepth: Math.round((window.pageYOffset / (document.body.scrollHeight - window.innerHeight)) * 100),
      ...pageMetrics,
    }

    // Track page exit event
    const eventType = `page_exit_${exitReason}`.replace(/[^a-zA-Z0-9_]/g, '_')
    window[RUM_CONFIG.functionName]('addEvent', eventType)

    addRumBreadcrumb(`Page Exit: ${exitReason} (${performanceData.timeOnPage}ms)`, 'lifecycle')
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track page exit:', error)
  }
}

/**
 * Track custom user events
 * @param {string} eventName - Custom event name
 * @param {string} category - Event category (optional)
 */
export const trackCustomEvent = (eventName, category = 'custom') => {
  if (!isRumAvailable()) return

  try {
    // Use correct Site24x7 custom events API
    const eventType = `${category}_${eventName}`.replace(/[^a-zA-Z0-9_]/g, '_')
    window[RUM_CONFIG.functionName]('addEvent', eventType)

    addRumBreadcrumb(`Custom Event: ${eventName}`, category)
  } catch (error) {
    console.error('Site24x7 RUM: Failed to track custom event:', error)
  }
}

/**
 * Initialize comprehensive user tracking
 * Sets up automatic tracking for common user interactions
 */
export const initializeUserTracking = () => {
  if (!isRumAvailable()) return

  try {
    // Set page load time for exit tracking
    window.pageLoadTime = Date.now()

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        trackCustomEvent('page_hidden', { timestamp: new Date().toISOString() }, 'page_lifecycle')
      } else {
        trackCustomEvent('page_visible', { timestamp: new Date().toISOString() }, 'page_lifecycle')
      }
    })

    // Track page unload (user leaving)
    window.addEventListener('beforeunload', () => {
      trackPageExit('page_unload', {
        timeOnPage: Date.now() - window.pageLoadTime,
        finalScrollPosition: window.pageYOffset,
      })
    })

    // Track scroll depth milestones
    let maxScroll = 0
    const scrollMilestones = [25, 50, 75, 100]

    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round((window.pageYOffset / (document.body.scrollHeight - window.innerHeight)) * 100)

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent

        // Track scroll milestones
        scrollMilestones.forEach((milestone) => {
          if (scrollPercent >= milestone && maxScroll < milestone) {
            // Direct Site24x7 API call for better performance
            window[RUM_CONFIG.functionName]('addEvent', `scroll_milestone_${milestone}`)

            addRumBreadcrumb(`Scroll milestone: ${milestone}%`, 'user_engagement')
          }
        })
      }
    })

    // Track clicks on buttons and links automatically
    document.addEventListener('click', (event) => {
      const element = event.target
      const tagName = element.tagName.toLowerCase()

      if (tagName === 'button' || tagName === 'a' || element.onclick) {
        const elementId = element.id || element.className || 'unknown'
        const elementText = element.textContent?.trim()?.substring(0, 50) || element.title || 'no text'

        trackUserClick(tagName, elementId, elementText, {
          href: element.href,
          type: element.type,
          classList: Array.from(element.classList),
        })
      }
    })
  } catch (error) {
    console.error('Site24x7 RUM: Failed to initialize user tracking:', error)
  }
}

//Check if RUM is initialized and available
export const isRumAvailable = () => {
  return rumInitialized && typeof window[RUM_CONFIG.functionName] === 'function'
}

//Get current environment info for debugging
export const getEnvironmentInfo = () => {
  const appKey = getRumAppKey()
  return {
    environment: getCurrentEnvironment(),
    rumEnabled: shouldEnableRum(),
    rumInitialized,
    rumAvailable: isRumAvailable(),
    currentUrl: window.location.href,
    hostname: window.location.hostname,
    appKey: appKey ? appKey.substring(0, 10) + '...' : 'Not configured',
    hasValidKey: appKey && !appKey.includes('YOUR_'),
    config: RUM_CONFIG.options,
  }
}

//Get RUM configuration for debugging
export const getRumConfig = () => {
  return {
    ...RUM_CONFIG,
    appKeys: Object.keys(RUM_APP_KEYS).reduce((acc, env) => {
      acc[env] = RUM_APP_KEYS[env].includes('YOUR_') ? 'Not configured' : 'Configured'
      return acc
    }, {}),
  }
}

export default {
  initSite24x7Rum,
  setRumUserId,
  addRumBreadcrumb,
  isRumAvailable,
  shouldEnableRum,
  getRumAppKey,
  getCurrentEnvironment,
  getEnvironmentInfo,
  getRumConfig,
  // Add new tracking functions
  trackPageNavigation,
  trackUserClick,
  trackFormInteraction,
  trackUserSession,
  trackPageExit,
  trackCustomEvent,
  initializeUserTracking,
  formatUserIdentifier,
}
